import { useEffect, useRef, useState } from "react";
import { TOOLS, usePrefersReducedMotion, useReveal } from "@authoxi/ui-shared";
import { CHAIN, EVENTS, HERO, LADDER, PILLARS, STANDARDS, VERDICT_CARDS } from "../content";
import { Ask } from "../components/Ask";
import { Facts } from "../components/Facts";
import { Quickstart } from "../components/Quickstart";

export function Home() {
  useReveal();
  return (
    <>
      <Hero />
      <FourWords />
      <OneQuestion />
      <Chain />
      <Adoption />
      <Quickstart />
      <Facts />
      <Ask id="ask-home" source="home" />
    </>
  );
}

function Hero() {
  return (
    <section className="hero">
      <div className="grid-bg" />
      <div className="wide">
        <div className="hero-grid">
          <div>
            <span className="pill">
              <span className="dot" style={{ color: "var(--allow-text)" }} />
              {HERO.stage}
            </span>
            <h1 className="mega">
              {HERO.h1a}
              <span className="l2">{HERO.h1b}</span>
              <Cycle />
            </h1>
            <p className="lede">{HERO.lede}</p>
            <div className="cta-row">
              <a className="btn btn-go btn-lg" href="/start">
                Get a key — free
              </a>
              <a className="btn btn-lg" href={`${TOOLS}/grader`}>
                Grade an agent first
              </a>
            </div>
            <p className="hero-note">
              <strong style={{ color: "var(--ink)" }}>{HERO.note}</strong>
            </p>
            <p className="hero-note" style={{ marginTop: "var(--s2)" }}>
              {HERO.note2}
            </p>
          </div>
          <div>
            <Ledger />
            <p className="xs muted" style={{ marginTop: "var(--s3)", textAlign: "center" }}>
              Watch the escalation get approved. Sample fleet — the four outcomes, the signing
              and the resolve are the real ones.
            </p>
          </div>
        </div>
        <div className="standards" style={{ marginTop: "var(--s7)" }}>
          <span style={{ color: "var(--ink)" }}>Built on</span>
          {STANDARDS.map((s) => (
            <span key={s}>{s}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * The headline's last word cycles the four verdicts.
 *
 * The span is REPLACED, not re-labelled: setting textContent alone changes the word with no
 * motion, because the CSS animation only restarts when the element does. React's key does that
 * for us — which is the whole reason the key is the word.
 */
function Cycle() {
  const WORDS = [
    ["ALLOW", "var(--allow)"],
    ["DENY", "var(--deny)"],
    ["ESCALATE", "var(--escalate)"],
    ["CHALLENGE", "var(--challenge)"],
  ] as const;
  const reduced = usePrefersReducedMotion();
  const [i, setI] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => setI((n) => n + 1), 1900);
    return () => clearInterval(t);
  }, [reduced]);

  const [word, colour] = WORDS[i % WORDS.length];
  return (
    <span className="cycle" id="cycle" style={{ color: colour }} data-testid="cycle">
      <span key={word}>{word}</span>
    </span>
  );
}

/**
 * The live ledger — the hero's proof: the product running, not a screenshot of it.
 *
 * The escalation **resolves in place**, and the CHIP has to change with the row. That is not a
 * detail: the row once turned green on the line before the throw and sat there claiming
 * approval with its chip still reading `escalate`, for 1400ms after every page load. The row
 * changing verdict is the entire claim, and two separate rows could not make it.
 */
function Ledger() {
  const MAX = 6;
  const reduced = usePrefersReducedMotion();
  const [rows, setRows] = useState(() =>
    Array.from({ length: MAX - 1 }, (_, n) => ({
      ...EVENTS[(EVENTS.length - (MAX - 1 - n)) % EVENTS.length],
      id: `seed-${n}`,
      resolved: false,
    })),
  );
  const next = useRef(0);

  useEffect(() => {
    // Seeded full rather than filling up live: an empty panel that populates over ten seconds
    // is three-quarters blank during exactly the window in which a visitor decides whether the
    // page has finished loading.
    const add = () => {
      const e = EVENTS[next.current++ % EVENTS.length];
      const id = `ev-${next.current}`;
      setRows((r) => [{ ...e, id, resolved: false }, ...r].slice(0, MAX));
      if ("resolves" in e && e.resolves) {
        setTimeout(
          () => setRows((r) => r.map((x) => (x.id === id ? { ...x, resolved: true } : x))),
          1400,
        );
      }
    };
    add();
    if (reduced) return;
    const t = setInterval(add, 2200);
    return () => clearInterval(t);
  }, [reduced]);

  return (
    <div className="ledger">
      <div className="ledger-head">
        <span className="label" style={{ color: "var(--ink)" }}>
          Decision ledger
        </span>
        <span className="pill">
          <span className="dot" style={{ color: "var(--allow-text)" }} />
          signing
        </span>
      </div>
      <div className="ledger-body" id="ledger" data-testid="ledger">
        {rows.map((e) => {
          const done = e.resolved && "resolves" in e && e.resolves;
          const verdict = done ? e.resolves!.verdict : e.verdict;
          return (
            <div className={`ev${done ? " resolved" : ""}`} key={e.id} data-testid="ledger-row">
              <div>
                <div className="ev-agent">{e.agent}</div>
                <div className="ev-action">{e.action}</div>
                <div className="ev-note">{e.note}</div>
                {done && (
                  <div className="ev-signed">
                    <span className="dot" />
                    {e.resolves!.note}
                  </div>
                )}
              </div>
              {/* The chip reads the same value the row does. There is no second source for it
                  to fall out of step with. */}
              <span className={`v dim v-${verdict}`} data-testid="ledger-chip">
                {verdict}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FourWords() {
  return (
    <section className="section">
      <div className="wide">
        <div className="rule">
          <span className="label">The whole product, in four words</span>
        </div>
        <div className="four">
          {VERDICT_CARDS.map(([v, title, body, note], i) => (
            <article className={`vcard vc-${v}`} key={v}>
              <span
                className={`v v-${v}`}
                style={{
                  alignSelf: "flex-start",
                  background: v === "deny" ? "rgba(255,255,255,.2)" : "rgba(0,0,0,.14)",
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3>{title}</h3>
              <p>{body}</p>
              <div className="n">{note}</div>
            </article>
          ))}
        </div>
        <p className="sm muted" style={{ marginTop: "var(--s5)", maxWidth: "var(--measure)" }}>
          Treating the fourth as a denial is the single easiest mistake to make against this API,
          so it is a separate exception type rather than a subclass — and there is a test that
          proves it.
        </p>
      </div>
    </section>
  );
}

function OneQuestion() {
  return (
    <section className="section">
      <div className="wide">
        <div className="rule">
          <span className="label">One question</span>
        </div>
        <div style={{ maxWidth: "34ch" }}>
          <p className="xl" style={{ fontSize: "clamp(1.5rem,3vw,2rem)", lineHeight: 1.25 }}>
            May this actor do this, right now — and who is accountable?
          </p>
        </div>
        <p className="lede" style={{ marginTop: "var(--s5)" }}>
          Identity has always assumed one kind of actor and split the work three ways: SSO asks{" "}
          <em>right human?</em>, CAPTCHA asks <em>not a bot?</em>, API keys were how machines
          snuck in. Agents act as a chain — human → agent → sub-agent → action — and the three
          old questions collapse into one that nobody owns.
        </p>
        <div className="pillars" style={{ marginTop: "var(--s7)" }}>
          {PILLARS.map((p) => (
            <article className="pillar reveal" key={p.n}>
              <span className="n">
                {p.n} · {p.governs}
              </span>
              <h3>{p.name}</h3>
              <p>{p.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Chain() {
  return (
    <section className="section" style={{ background: "var(--bg-2)" }}>
      <div className="wide">
        <div className="rule">
          <span className="label">The chain</span>
        </div>
        <h2 className="xl" style={{ maxWidth: "20ch", marginBottom: "var(--s3)" }}>
          Authority that narrows at every hop.
        </h2>
        <p className="lede" style={{ marginBottom: "var(--s6)" }}>
          A sub-agent cannot be granted more than its parent holds, and cannot outlive it. That is
          checked at issuance, not documented as a guideline.
        </p>
        <div className="chain">
          {CHAIN.map((c) => (
            <div className="node" key={c.who}>
              <div className="who">{c.who}</div>
              <div className="what">{c.what}</div>
              <div className="cap">{c.cap}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Adoption() {
  return (
    <section className="section">
      <div className="wide">
        <div className="rule">
          <span className="label">Adoption</span>
        </div>
        <h2 className="xl" style={{ maxWidth: "22ch", marginBottom: "var(--s6)" }}>
          Start where nothing can break.
        </h2>
        <div className="stack">
          {LADDER.map((r) => (
            <div className="rung reveal" key={r.lv}>
              <span className="lv">{r.lv}</span>
              <div>
                <strong>{r.name}</strong>
                <p>{r.body}</p>
              </div>
              <span className="status live">Live</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
